"use client";

import { useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Bot, X } from "lucide-react";
import { mutation } from "@/lib/api";
import { useResource } from "@/lib/use-resource";
import { useSession } from "@/components/session-provider";
import { ServerAssistant } from "@/components/server-assistant";
import { toast } from "@/components/toast-provider";

type ServerRef = { id: string; name: string };

// Floating assistant available on every panel page. It follows the server in
// the URL, or lets the user pick one elsewhere.
export function AssistantLauncher() {
  const session = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [picked, setPicked] = useState("");
  const available = useResource<{ available: boolean; name: string; position: string; accentColor: string; showBadge: boolean }>("/assistant", 0, !!session.user);
  const routeId = /^\/servers\/([^/]+)/.exec(pathname)?.[1];
  const servers = useResource<{ servers: ServerRef[] }>("/servers", 0, open && !routeId);
  const serverId = routeId ? decodeURIComponent(routeId) : picked;

  if (!available.data?.available) return null;

  const openTab = (tab: string) => {
    const base = `/servers/${encodeURIComponent(serverId)}`;
    router.push(tab === "files" ? `${base}/files` : `${base}?tab=${tab === "plugins" ? "plugin-check" : tab}`);
  };
  const power = async (action: string) => {
    await mutation(`/servers/${encodeURIComponent(serverId)}/power`, { action });
    toast({ tone: "success", title: `Server ${action} requested` });
  };

  return (
    <div className="assistant-overlay" data-position={available.data.position} style={available.data.accentColor ? ({ "--assistant-accent": available.data.accentColor } as React.CSSProperties) : undefined}>
      <section className="assistant-window" hidden={!open} aria-label={available.data.name}>
        <button className="icon-button assistant-close" title="Close assistant" onClick={() => setOpen(false)}><X size={16} /></button>
        {!serverId ? (
          <div className="assistant-pick">
            <h2><Bot size={18} /> {available.data.name}</h2>
            <p className="muted">Choose a server to talk about.</p>
            <select value={picked} onChange={(event) => setPicked(event.target.value)}>
              <option value="">Select a server</option>
              {servers.data?.servers.map((server) => <option key={server.id} value={server.id}>{server.name}</option>)}
            </select>
          </div>
        ) : (
          <ServerAssistant
            key={serverId}
            id={serverId}
            canReadLogs={session.can("file.read")}
            onPower={power}
            onNavigate={openTab}
            onReply={() => !open && setUnread((count) => count + 1)}
          />
        )}
      </section>
      <button
        className="assistant-bubble"
        aria-label={open ? "Close assistant" : "Open assistant"}
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          setUnread(0);
        }}
      >
        <Bot size={26} />
        {available.data.showBadge && unread > 0 && <span className="assistant-badge">{unread}</span>}
      </button>
    </div>
  );
}
